import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaArrowRight, FaCamera, FaMicrochip, FaBatteryFull } from 'react-icons/fa';

const ProductCard = ({ product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const images = product.images && product.images.length > 0 
    ? product.images 
    : [{ id: 0, image_path: '/Photo/i7 air 2.jpg', label: 'Device View' }];

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div 
      className="peace-product-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top Floating Badge Bar */}
      <div className="card-top-bar">
        <span className="card-series-pill">
          {product.series || 'Peace Phone'}
        </span>
        {product.isNew && (
          <span className="card-new-badge">New</span>
        )}
      </div>

      {/* Image Stage with Intuitive Angle Carousel */}
      <div className="card-image-stage">
        <Link to={`/products/${product.id}`} className="card-image-link" aria-label={product.name}>
          <img 
            src={images[currentImageIndex].image_path} 
            alt={`${product.name} - ${images[currentImageIndex].label || 'View'}`}
            className="card-main-image"
            loading="lazy"
          />
        </Link>

        {/* Carousel Arrow Controls (Visible when more than 1 image exists) */}
        {images.length > 1 && (
          <>
            <button 
              className={`carousel-nav-btn prev-btn ${isHovered ? 'visible' : ''}`}
              onClick={prevImage}
              aria-label="Previous photo angle"
            >
              <FaChevronLeft />
            </button>
            <button 
              className={`carousel-nav-btn next-btn ${isHovered ? 'visible' : ''}`}
              onClick={nextImage}
              aria-label="Next photo angle"
            >
              <FaChevronRight />
            </button>

            {/* Micro Dot Indicators */}
            <div className="carousel-dots-row">
              {images.map((img, idx) => (
                <button
                  key={img.id || idx}
                  className={`carousel-dot ${idx === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex(idx);
                  }}
                  aria-label={`View photo ${idx + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Available Color Swatches Preview */}
      {product.colors && product.colors.length > 0 && (
        <div className="card-color-swatches" title="Available Finishes">
          {product.colors.map((color, idx) => (
            <span 
              key={idx}
              className="color-dot"
              style={{ backgroundColor: color.hex }}
              title={color.name}
            />
          ))}
          <span className="color-count-label">
            {product.colors.length} {product.colors.length === 1 ? 'color' : 'colors'}
          </span>
        </div>
      )}

      {/* Content & Typography */}
      <div className="card-body-content">
        <div className="card-title-group">
          <h3 className="card-product-title">
            <Link to={`/products/${product.id}`} className="title-link">
              {product.name}
            </Link>
          </h3>
          {product.tagline && (
            <p className="card-product-tagline">{product.tagline}</p>
          )}
        </div>

        {/* Benefit-Driven Description */}
        <p className="card-product-desc">
          {product.description}
        </p>

        {/* Key Spec Micro-Pills */}
        {product.keyFeatures && product.keyFeatures.length > 0 && (
          <div className="card-spec-pills">
            <div className="spec-pill-item">
              <FaMicrochip className="pill-mini-icon" />
              <span>{product.keyFeatures[1] || 'Fast Silicon'}</span>
            </div>
            <div className="spec-pill-item">
              <FaCamera className="pill-mini-icon" />
              <span>{product.keyFeatures[2] || 'Pro Camera'}</span>
            </div>
            {product.keyFeatures[3] && (
              <div className="spec-pill-item">
                <FaBatteryFull className="pill-mini-icon" />
                <span>{product.keyFeatures[3]}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Card Footer with Price & Aligned CTA Actions */}
      <div className="card-footer-actions">
        <div className="card-pricing-block">
          <div className="price-main">{product.price}</div>
          {product.monthlyPrice && (
            <div className="price-monthly">{product.monthlyPrice}</div>
          )}
        </div>

        <div className="card-button-group">
          <Link 
            to={`/products/${product.id}`} 
            className="card-cta-btn secondary-btn"
          >
            View Details
          </Link>
          <Link 
            to={`/products/${product.id}`} 
            className="card-cta-btn primary-btn"
          >
            Buy <FaArrowRight className="btn-arrow" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
